/**
 * Hover preview + scroll horizontal de variantes en tarjetas PLP / recomendaciones.
 */
(function () {
  // Evita listeners/observers duplicados si el script aparece varias veces (cards + sección).
  if (window.__lopVarianteDeProductoInit) return;
  window.__lopVarianteDeProductoInit = true;

  /** @type {WeakMap<HTMLElement, { src: string; srcset: string }>} */
  const originalImages = new WeakMap();
  // WeakSet: un slide clonado por un carrusel arrastra atributos pero no listeners.
  const boundThumbs = new WeakSet();
  const boundRoots = new WeakSet();
  /** @type {Set<() => void>} */
  const scrollerRefreshers = new Set();
  const GAP = 4;

  /**
   * @param {HTMLAnchorElement} thumb
   * @returns {HTMLImageElement | null}
   */
  function getCardImage(thumb) {
    const productCard = thumb.closest('product-card');
    if (!productCard) return null;

    const activeSlide = productCard.querySelector(
      'slideshow-slide[aria-hidden="false"] img, .card-gallery img, .product-card-gallery img'
    );

    return activeSlide instanceof HTMLImageElement ? activeSlide : null;
  }

  /**
   * @param {HTMLImageElement} image
   */
  function storeOriginal(image) {
    if (originalImages.has(image)) return;

    originalImages.set(image, {
      src: image.currentSrc || image.src,
      srcset: image.srcset || '',
    });
  }

  /**
   * @param {HTMLImageElement} image
   */
  function restoreOriginal(image) {
    const original = originalImages.get(image);
    if (!original) return;

    image.src = original.src;
    if (original.srcset) {
      image.srcset = original.srcset;
    } else {
      image.removeAttribute('srcset');
    }
  }

  /**
   * @param {PointerEvent} event
   */
  function handlePointerEnter(event) {
    const thumb = event.currentTarget;
    if (!(thumb instanceof HTMLAnchorElement)) return;

    const previewSrc = thumb.dataset.previewSrc;
    if (!previewSrc) return;

    const image = getCardImage(thumb);
    if (!image) return;

    storeOriginal(image);
    image.src = previewSrc;
    image.removeAttribute('srcset');
  }

  /**
   * @param {PointerEvent} event
   */
  function handlePointerLeave(event) {
    const thumb = event.currentTarget;
    if (!(thumb instanceof HTMLAnchorElement)) return;

    const image = getCardImage(thumb);
    if (!image) return;

    restoreOriginal(image);
  }

  /**
   * @param {ParentNode} root
   */
  function bindThumbnails(root) {
    root.querySelectorAll('[data-lop-variante-context="card"] .lop-variante-de-producto__thumb[data-preview-src]').forEach((thumb) => {
      if (!(thumb instanceof HTMLAnchorElement)) return;
      if (boundThumbs.has(thumb)) return;

      boundThumbs.add(thumb);
      thumb.addEventListener('pointerenter', handlePointerEnter);
      thumb.addEventListener('pointerleave', handlePointerLeave);
      thumb.addEventListener('focus', handlePointerEnter);
      thumb.addEventListener('blur', handlePointerLeave);
    });
  }

  /**
   * @param {HTMLElement} list
   * @returns {number}
   */
  function getScrollStep(list) {
    const first = list.firstElementChild;
    const itemWidth = first ? first.getBoundingClientRect().width : 0;
    const step = itemWidth + GAP;

    if (step <= 0) return list.clientWidth;

    return Math.max(1, Math.floor(list.clientWidth / step)) * step;
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement} list
   * @param {HTMLButtonElement | null} prev
   * @param {HTMLButtonElement | null} next
   */
  function updateNav(root, list, prev, next) {
    const maxScroll = list.scrollWidth - list.clientWidth;

    root.classList.toggle('has-overflow', maxScroll > 1);

    const focused = document.activeElement;

    if (prev) prev.disabled = list.scrollLeft <= 1;
    if (next) next.disabled = list.scrollLeft >= maxScroll - 1;

    if (focused === prev && prev?.disabled && next && !next.disabled) next.focus();
    if (focused === next && next?.disabled && prev && !prev.disabled) prev.focus();
  }

  function refreshAllScrollers() {
    scrollerRefreshers.forEach((refresh) => refresh());
  }

  /**
   * @param {ParentNode} scope
   */
  function bindScrollers(scope) {
    scope.querySelectorAll('.lop-variante-de-producto--card').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      if (boundRoots.has(root)) return;

      const list = root.querySelector('.lop-variante-de-producto__list');
      if (!(list instanceof HTMLElement)) return;

      const prev = root.querySelector('.lop-variante-de-producto__nav--prev');
      const next = root.querySelector('.lop-variante-de-producto__nav--next');

      boundRoots.add(root);

      const refresh = () => {
        if (!root.isConnected) {
          scrollerRefreshers.delete(refresh);
          return;
        }
        updateNav(root, list, prev instanceof HTMLButtonElement ? prev : null, next instanceof HTMLButtonElement ? next : null);
      };

      scrollerRefreshers.add(refresh);

      /**
       * @param {MouseEvent} event
       * @param {number} direction
       */
      const scrollBy = (event, direction) => {
        event.preventDefault();
        event.stopPropagation();
        list.scrollBy({ left: direction * getScrollStep(list), behavior: 'smooth' });
      };

      prev?.addEventListener('click', (event) => scrollBy(event, -1));
      next?.addEventListener('click', (event) => scrollBy(event, 1));

      list.addEventListener('scroll', refresh, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(refresh).observe(list);
      }

      // En PDP el layout del carousel de recomendaciones termina después del bind inicial.
      requestAnimationFrame(refresh);
      window.setTimeout(refresh, 100);
      window.setTimeout(refresh, 400);
      window.setTimeout(refresh, 1000);
      if (document.readyState !== 'complete') {
        window.addEventListener('load', refresh, { once: true });
      }
    });
  }

  function bindAll(scope) {
    bindThumbnails(scope);
    bindScrollers(scope);
  }

  /**
   * Cards inyectadas por AJAX (product-recommendations, filtros, etc.).
   */
  function observeNewCards() {
    if (typeof MutationObserver === 'undefined') return;

    let scheduled = false;

    const scheduleBind = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        bindAll(document);
        refreshAllScrollers();
        // Segundo pase cuando el slideshow/carousel ya midió anchos.
        window.setTimeout(() => {
          bindAll(document);
          refreshAllScrollers();
        }, 350);
      });
    };

    const observer = new MutationObserver((mutations) => {
      const hasCards = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches('.lop-variante-de-producto--card') ||
              node.querySelector('.lop-variante-de-producto--card'))
        )
      );

      if (hasCards) scheduleBind();
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Hook directo al custom element de recomendaciones (innerHTML replace).
    document.querySelectorAll('product-recommendations').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;

      const attrObserver = new MutationObserver(() => scheduleBind());
      attrObserver.observe(el, {
        attributes: true,
        attributeFilter: ['data-recommendations-performed'],
        childList: true,
        subtree: true,
      });
    });
  }

  function init() {
    bindAll(document);
    observeNewCards();

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target instanceof HTMLElement) {
        bindAll(event.target);
        refreshAllScrollers();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
